"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import NavBar from "../../home-page/components/NavBar";
import FooterSection from "../../home-page/components/FooterSection";
import { generateBreadcrumbSchema, generateOrganizationSchema } from '@/lib/utils/metadata';
import { testimonialService, Testimonial } from '@/lib/services/testimonialService';
import { landingPageService, LandingPageContent } from '@/lib/services/landingPageService';
import AppIcon from '@/components/ui/AppIcon';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';

// Helper function to convert kebab-case icon names to PascalCase Heroicon names
const convertIconName = (iconName: string): string => {
  if (!iconName || typeof iconName !== 'string') return 'QuestionMarkCircleIcon';
  
  // Convert kebab-case to PascalCase and add 'Icon' suffix
  // e.g., 'arrows-up-down' -> 'ArrowsUpDownIcon'
  return iconName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('') + 'Icon';
};

export default function DriversLandingPage() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(true);
  const [heroStats, setHeroStats] = useState<any>(null);
  const [benefits, setBenefits] = useState<any[]>([]);
  const [requirements, setRequirements] = useState<any[]>([]);
  const [howItWorks, setHowItWorks] = useState<any[]>([]);
  const [earnings, setEarnings] = useState<any[]>([]);
  const [contentError, setContentError] = useState<string | null>(null);

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const data = await testimonialService.getActive();
        setTestimonials(data.slice(0, 3)); // Show top 3 testimonials
      } catch (error) {
        console.error('Failed to load testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTestimonials();
  }, []);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setContentLoading(true);
        setContentError(null);
        const content = await landingPageService.getByPageKey('drivers');
        
        // Map content by section key
        content.forEach((item: LandingPageContent) => {
          if (!item.isActive) return;
          
          switch (item.sectionKey) {
            case 'hero':
              setHeroStats(item.content);
              break;
            case 'benefits':
              setBenefits(item.content?.items || []);
              break;
            case 'requirements':
              setRequirements(item.content?.items || []);
              break;
            case 'howItWorks':
              setHowItWorks(item.content?.items || []);
              break;
            case 'earnings':
              setEarnings(item.content?.items || []);
              break;
          }
        });
      } catch (error) {
        console.error('Failed to load landing page content:', error);
        setContentError('Failed to load content');
        // Set fallback content
        setFallbackContent();
      } finally {
        setContentLoading(false);
      }
    };

    loadContent();
  }, []);

  const setFallbackContent = () => {
    setHeroStats({
      drivers: "500+",
      hourlyRate: "$25-$40",
      cities: "5 Cities"
    });

    setBenefits([
      {
        icon: (
          <svg width="28" height="28" fill="none" stroke="#48794D" strokeWidth="2" viewBox="0 0 24 24">
            <line x1="12" y1="1" x2="12" y2="23" />
            <polyline points="17 6 12 1 7 6" />
            <polyline points="7 18 12 23 17 18" />
          </svg>
        ),
        title: "Flexible Schedule",
        description: "Work when you want. Choose your own hours and be your own boss. Perfect for full-time or part-time."
      },
      {
        icon: (
          <svg width="28" height="28" fill="none" stroke="#48794D" strokeWidth="2" viewBox="0 0 24 24">
            <line x1="12" y1="2" x2="12" y2="22" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
          </svg>
        ),
        title: "Competitive Earnings",
        description: "Earn $25-$40 per hour on average. Keep 100% of your tips. Weekly direct deposits."
      }
    ]);

    setRequirements([
      {
        icon: (
          <svg width="24" height="24" fill="none" stroke="#48794D" strokeWidth="2" viewBox="0 0 24 24">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ),
        text: "Valid driver's license (G or G2)"
      },
      {
        icon: (
          <svg width="24" height="24" fill="none" stroke="#48794D" strokeWidth="2" viewBox="0 0 24 24">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ),
        text: "Access to a vehicle (truck, van, or SUV preferred)"
      }
    ]);

    setHowItWorks([
      {
        step: "1",
        title: "Apply Online",
        description: "Complete our simple application in 10 minutes. Tell us about yourself and your vehicle."
      },
      {
        step: "2",
        title: "Background Check",
        description: "We'll verify your license and conduct a background check. Usually takes 2-3 business days."
      }
    ]);

    setEarnings([
      {
        title: "Average Hourly",
        amount: "$25-$40",
        description: "Per hour during active deliveries"
      },
      {
        title: "Weekly Potential",
        amount: "$800-$1,600",
        description: "Based on 20-40 hours per week"
      }
    ]);
  };

  return (
    <>
      {/* Structured Data - WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Be a Driver - Earn Flexible Income',
            description: 'Join 500+ drivers earning great money with GoGedit. Flexible schedule, competitive pay, and full support.',
            url: `${SITE_URL}/landing/drivers`,
            breadcrumb: generateBreadcrumbSchema([
              { name: 'Home', url: `${SITE_URL}/home-page` },
              { name: 'Landing', url: `${SITE_URL}/landing` },
              { name: 'Drivers', url: `${SITE_URL}/landing/drivers` }
            ])
          })
        }}
      />

      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateOrganizationSchema())
        }}
      />

      <NavBar />

      {/* Hero Section */}
      <section className="hero-gradient relative overflow-hidden py-20 md:py-32">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-brand-green/5 -translate-y-1/4 translate-x-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-brand-purple/5 translate-y-1/4 -translate-x-1/4 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-brand-green/20 shadow-green-md">
              <span className="w-2 h-2 rounded-full bg-brand-green animate-pulse" />
              <span className="text-sm font-semibold text-dark-navy">For Drivers</span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight text-deep-navy">
              Drive. Earn. <span className="text-brand-green">Thrive.</span>
            </h1>

            <p className="text-lg md:text-xl text-body-text leading-relaxed">
              {contentLoading ? (
                <span className="inline-block w-64 h-6 bg-gray-200 animate-pulse rounded" />
              ) : (
                `Join ${heroStats?.drivers || '500+'} drivers earning ${heroStats?.hourlyRate || '$25-$40/hour'} with flexible schedules across Ontario. Be your own boss and get paid weekly.`
              )}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
              <Link href="/be-a-driver" className="btn-green">
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <rect x="1" y="3" width="15" height="13" rx="2" />
                  <path d="M16 8h4l3 5v3h-7V8z" />
                  <circle cx="5.5" cy="18.5" r="2.5" />
                  <circle cx="18.5" cy="18.5" r="2.5" />
                </svg>
                Apply to Drive
              </Link>
              <Link href="/contact-us" className="btn-secondary">
                Learn More
              </Link>
            </div>

            {contentLoading ? (
              <div className="flex items-center justify-center gap-8 pt-6">
                <div className="w-20 h-16 bg-gray-200 animate-pulse rounded" />
                <div className="w-px h-10 bg-border-gray" />
                <div className="w-20 h-16 bg-gray-200 animate-pulse rounded" />
                <div className="w-px h-10 bg-border-gray" />
                <div className="w-20 h-16 bg-gray-200 animate-pulse rounded" />
              </div>
            ) : (
              <div className="flex items-center justify-center gap-8 pt-6">
                <div className="text-center">
                  <p className="font-heading font-800 text-2xl text-brand-green">{heroStats?.drivers || '500+'}</p>
                  <p className="text-xs text-muted-text font-medium uppercase tracking-wider mt-1">Drivers</p>
                </div>
                <div className="w-px h-10 bg-border-gray" />
                <div className="text-center">
                  <p className="font-heading font-800 text-2xl text-brand-purple">{heroStats?.hourlyRate || '$25-$40'}</p>
                  <p className="text-xs text-muted-text font-medium uppercase tracking-wider mt-1">Per Hour</p>
                </div>
                <div className="w-px h-10 bg-border-gray" />
                <div className="text-center">
                  <p className="font-heading font-800 text-2xl text-brand-green">{heroStats?.cities || '5 Cities'}</p>
                  <p className="text-xs text-muted-text font-medium uppercase tracking-wider mt-1">Coverage</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Earnings Potential Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
              Your <span className="text-brand-green">Earning</span> Potential
            </h2>
            <p className="text-lg text-body-text">
              Real numbers from real drivers. Your earnings depend on hours worked and demand.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {earnings.map((item, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-brand-green/5 to-brand-green/10 rounded-2xl p-8 text-center border-2 border-brand-green/20 hover:border-brand-green/40 transition-all duration-300 hover:-translate-y-1"
              >
                <p className="text-sm font-semibold text-brand-green uppercase tracking-wider mb-2">
                  {item.title}
                </p>
                <p className="font-heading text-4xl md:text-5xl font-bold text-deep-navy mb-2">
                  {item.amount}
                </p>
                <p className="text-body-text text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-light-blue rounded-2xl p-6 border border-brand-purple/10">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-brand-purple/10 flex items-center justify-center flex-shrink-0">
                <svg width="20" height="20" fill="none" stroke="#4732D7" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4" />
                  <path d="M12 8h.01" />
                </svg>
              </div>
              <div>
                <p className="font-heading font-bold text-deep-navy mb-1">Earnings Disclaimer</p>
                <p className="text-body-text text-sm leading-relaxed">
                  Earnings vary based on location, time of day, demand, and hours worked. Figures shown are averages and not guaranteed. You are an independent contractor responsible for your own expenses.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
              Why Drive with <span className="text-brand-green">GoGedit</span>?
            </h2>
            <p className="text-lg text-body-text">
              Join a community of drivers earning great money with flexible schedules.
            </p>
          </div>

          {contentLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-200 animate-pulse rounded-2xl h-48" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border border-border-gray/50"
                >
                  <div className="w-14 h-14 rounded-xl bg-brand-green/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    {typeof benefit.icon === 'string' ? (
                      <AppIcon 
                        name={convertIconName(benefit.icon)} 
                        size={28} 
                        className="text-brand-green" 
                      />
                    ) : (
                      benefit.icon
                    )}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-deep-navy mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-body-text leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 bg-light-green/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
              Driver <span className="text-brand-green">Requirements</span>
            </h2>
            <p className="text-lg text-body-text">
              Simple requirements to get started. Most drivers qualify easily.
            </p>
          </div>

          {contentLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-200 animate-pulse rounded-xl h-16" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {requirements.map((req, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-border-gray/50"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-green/10 flex items-center justify-center mt-0.5">
                    {typeof req.icon === 'string' ? (
                      <AppIcon 
                        name={convertIconName(req.icon)} 
                        size={16} 
                        className="text-brand-green" 
                      />
                    ) : (
                      req.icon
                    )}
                  </div>
                  <p className="text-body-text font-medium">{req.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
              How to <span className="text-brand-green">Get Started</span>
            </h2>
            <p className="text-lg text-body-text">
              Four simple steps to start earning with GoGedit.
            </p>
          </div>

          {contentLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-gray-200 animate-pulse rounded-2xl h-56" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {howItWorks.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-6 shadow-card h-full">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-green to-accent-green flex items-center justify-center text-white font-heading font-bold text-xl mb-4">
                      {item.step}
                    </div>
                    <h3 className="font-heading text-lg font-bold text-deep-navy mb-2">
                      {item.title}
                    </h3>
                    <p className="text-body-text text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                  {index < howItWorks.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-brand-green to-transparent" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Earnings Section */}
      <section className="py-20 bg-gradient-to-br from-brand-green to-accent-green text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Your Earning <span className="text-white/90">Potential</span>
            </h2>
            <p className="text-lg text-white/90">
              Real numbers from our driver community. Your earnings may vary.
            </p>
          </div>

          {contentLoading ? (
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white/10 animate-pulse rounded-2xl h-48" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {earnings.map((earning, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/20"
                >
                  <p className="text-sm font-semibold uppercase tracking-wider text-white/80 mb-2">
                    {earning.title}
                  </p>
                  <p className="font-heading text-4xl font-bold mb-2">
                    {earning.amount}
                  </p>
                  <p className="text-sm text-white/80">
                    {earning.description}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link href="/be-a-driver" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-brand-green rounded-xl font-semibold hover:bg-white/90 transition-colors shadow-lg">
              Start Earning Today
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {!loading && testimonials.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-deep-navy mb-4">
                What Our <span className="text-brand-green">Drivers</span> Say
              </h2>
              <p className="text-lg text-body-text">
                Real stories from drivers in our community.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 border border-border-gray/50"
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        width="16"
                        height="16"
                        fill={i < testimonial.rating ? "#F59E0B" : "#E5E7EB"}
                        viewBox="0 0 24 24"
                      >
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-body-text leading-relaxed mb-4">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center text-brand-green font-heading font-bold">
                      {testimonial.customerName.charAt(0)}
                    </div>
                    <div>
                      <p className="font-heading font-bold text-deep-navy text-sm">
                        {testimonial.customerName}
                      </p>
                      {testimonial.customerRole && (
                        <p className="text-muted-text text-xs">
                          {testimonial.customerRole}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-brand-green to-brand-green/80 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full bg-white blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Earning with GoGedit?
          </h2>
          <p className="text-white/90 text-lg mb-8 leading-relaxed">
            Join 500+ drivers who are already earning flexible income on their own schedule.
            Apply now and start driving within days.
          </p>
          <Link href="/be-a-driver" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-brand-green rounded-full font-heading font-bold text-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <rect x="1" y="3" width="15" height="13" rx="2" />
              <path d="M16 8h4l3 5v3h-7V8z" />
              <circle cx="5.5" cy="18.5" r="2.5" />
              <circle cx="18.5" cy="18.5" r="2.5" />
            </svg>
            Apply Now - Get Started Today
          </Link>
        </div>
      </section>

      {/* Cross-links to other landing pages */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-heading text-2xl font-bold text-deep-navy text-center mb-8">
            Explore More with GoGedit
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link href="/landing/users">
              <div className="group bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-brand-purple/20 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-purple/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <svg width="22" height="22" fill="none" stroke="#4732D7" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-dark-navy group-hover:text-brand-purple transition-colors">Need a Delivery?</h3>
                  <p className="text-sm text-body-text mt-1">Explore our user delivery services</p>
                </div>
                <svg width="18" height="18" fill="none" stroke="#4732D7" strokeWidth="2" viewBox="0 0 24 24" className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </div>
            </Link>
            <Link href="/landing/retailers">
              <div className="group bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-brand-green/20 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <svg width="22" height="22" fill="none" stroke="#48794D" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-dark-navy group-hover:text-brand-green transition-colors">Are You a Retailer?</h3>
                  <p className="text-sm text-body-text mt-1">Partner with us to grow your business</p>
                </div>
                <svg width="18" height="18" fill="none" stroke="#48794D" strokeWidth="2" viewBox="0 0 24 24" className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <FooterSection />
    </>
  );
}